import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Map, { Marker, NavigationControl } from "react-map-gl/mapbox";
import { motion } from "framer-motion";
import { Bed, Bath, Square, MapPin, Heart, Mail, Phone } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useAuth } from "../App";
import { api } from "../services/apiClient";
import {
  formatOperationTypes,
  formatPropertyPrice,
  formatPricePerM2,
  getPropertyPrice,
  isPriceOnRequest,
} from "../utils/formatters";
import { getMapboxToken } from "../utils/mapbox";

const MAPBOX_TOKEN = getMapboxToken();

const PropertyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [property, setProperty] = useState(null);
  const [developer, setDeveloper] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFavorite, setIsFavorite] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    budget_range: "",
    purchase_timeline: "",
    intended_use: "",
  });

  useEffect(() => {
    fetchProperty();
  }, [id]);

  const fetchProperty = async () => {
    try {
      const response = await api.get(`/properties/${id}`, { auth: false });
      if (!response.ok) throw new Error("Error al cargar propiedad");
      const data = await response.json();
      setProperty(data);

      // Registrar vista (sin esperar respuesta)
      api
        .post(`/properties/${id}/view`, undefined, { multipart: true })
        .catch(() => {});

      // Si tiene developer_id, obtener info del desarrollador
      if (data.developer_id) {
        fetchDeveloper(data.developer_id);
      }
    } catch (error) {
      toast.error("Error al cargar propiedad");
    } finally {
      setLoading(false);
    }
  };

  const fetchDeveloper = async (developerId) => {
    try {
      const response = await api.get(`/developers/${developerId}`, {
        auth: false,
      });
      if (response.ok) {
        const data = await response.json();
        setDeveloper(data);
      }
    } catch (error) {
      console.error("Error fetching developer:", error);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await api.post(
        "/contact",
        { ...contactForm, property_id: id },
        { multipart: false },
      );

      if (!response.ok) throw new Error("Error al enviar mensaje");

      toast.success("¡Mensaje enviado exitosamente!");
      setContactForm({ name: "", email: "", phone: "", message: "" });
    } catch (error) {
      toast.error(error.message);
    }
  };

  const toggleFavorite = async () => {
    if (!user) {
      toast.error("Debes iniciar sesión para agregar favoritos");
      return;
    }

    try {
      if (isFavorite) {
        await api.delete(`/favorites/${id}`);
        setIsFavorite(false);
        toast.success("Eliminado de favoritos");
      } else {
        await api.post("/favorites", { property_id: id });
        setIsFavorite(true);
        toast.success("Agregado a favoritos");
      }
    } catch (error) {
      toast.error("Error al actualizar favoritos");
    }
  };

  const scrollToContact = () => {
    const contactSection = document.getElementById("property-contact-card");
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="flex items-center justify-center py-20">
          <p className="text-lg text-muted-foreground">
            Propiedad no encontrada
          </p>
        </div>
      </div>
    );
  }

  const { price, currency } = getPropertyPrice(property);

  return (
    <div className="min-h-screen">
      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 md:py-8 pb-24 lg:pb-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
          <div className="lg:col-span-2 space-y-6 md:space-y-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              {/* Slideshow de imágenes */}
              {property.image_urls && property.image_urls.length > 1 ? (
                <div className="relative aspect-video rounded-lg overflow-hidden mb-4 md:mb-6">
                  <img
                    src={property.image_urls[currentImageIndex]}
                    alt={`${property.title} - ${currentImageIndex + 1}`}
                    className="w-full h-full object-cover"
                  />
                  <button
                    onClick={() =>
                      setCurrentImageIndex(
                        (currentImageIndex - 1 + property.image_urls.length) %
                          property.image_urls.length,
                      )
                    }
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/50 text-white p-3 rounded-full hover:bg-black/70 transition"
                  >
                    ←
                  </button>
                  <button
                    onClick={() =>
                      setCurrentImageIndex(
                        (currentImageIndex + 1) % property.image_urls.length,
                      )
                    }
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black/50 text-white p-3 rounded-full hover:bg-black/70 transition"
                  >
                    →
                  </button>
                  <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2">
                    {property.image_urls.map((_, index) => (
                      <div
                        key={index}
                        onClick={() => setCurrentImageIndex(index)}
                        className={`w-2 h-2 rounded-full cursor-pointer transition ${
                          index === currentImageIndex
                            ? "bg-white"
                            : "bg-white/50"
                        }`}
                      />
                    ))}
                  </div>
                </div>
              ) : (
                <div className="aspect-video rounded-lg overflow-hidden mb-4 md:mb-6">
                  <img
                    src={
                      property.image_urls?.[0] ||
                      "https://images.pexels.com/photos/35193584/pexels-photo-35193584.jpeg"
                    }
                    alt={property.title}
                    className="w-full h-full object-cover"
                    data-testid="property-image"
                  />
                </div>
              )}

              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-4 gap-4">
                <div className="flex-1">
                  <span className="text-sm uppercase tracking-wider font-semibold text-accent">
                    {property.category}
                    {property.property_type || property.subcategory
                      ? ` · ${property.property_type || property.subcategory}`
                      : ""}{" "}
                    ·{" "}
                    {formatOperationTypes(
                      property.operation_types,
                      property.status,
                    )}
                    {property.estado &&
                      ` · ${property.estado === "nuevo" ? "Nuevo" : property.estado === "segunda_mano" ? "Segunda Mano" : property.estado}`}
                  </span>
                  <h1
                    className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mt-2 tracking-tight"
                    style={{ fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}
                    data-testid="property-title"
                  >
                    {property.title}
                  </h1>
                </div>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={toggleFavorite}
                  className="flex-shrink-0"
                  data-testid="favorite-button"
                >
                  <Heart
                    className={`w-5 h-5 ${isFavorite ? "fill-red-500 text-red-500" : ""}`}
                  />
                </Button>
              </div>

              <p className="text-sm md:text-base text-muted-foreground flex items-center mb-6">
                <MapPin className="w-4 h-4 mr-2 flex-shrink-0" />
                {property.address}
              </p>

              {/* Precios en formato mejorado */}
              <div className="space-y-4 mb-6 md:mb-8">
                <div className="bg-primary/5 border-l-4 border-primary rounded-lg p-4">
                  <p className="text-sm text-muted-foreground mb-1">Precio</p>
                  <p
                    className="text-2xl md:text-3xl font-bold mono mb-2"
                    data-testid="property-price"
                  >
                    {formatPropertyPrice(property)}
                  </p>
                  {isPriceOnRequest(property) && (
                    <p className="text-sm text-muted-foreground">
                      Contacta al anunciante para recibir el precio y
                      condiciones.
                    </p>
                  )}
                  {property.construction_size_m2 &&
                    !isPriceOnRequest(property) &&
                    price && (
                      <p className="text-sm text-muted-foreground">
                        {formatPricePerM2(
                          price,
                          property.construction_size_m2,
                          currency,
                        )}{" "}
                        / m² construcción
                      </p>
                    )}
                </div>
              </div>

              {/* Especificaciones en 2 filas para residenciales */}
              <div className="space-y-3 mb-6 md:mb-8">
                {/* Fila 1: Terreno, Construcción, Estacionamientos */}
                <div className="grid grid-cols-3 gap-3 md:gap-4">
                  {property.land_size_m2 && (
                    <div className="bg-muted rounded-lg p-3 md:p-4">
                      <Square className="w-5 h-5 md:w-6 md:h-6 text-muted-foreground mb-2" />
                      <p className="text-lg md:text-2xl font-bold">
                        {property.land_size_m2.toLocaleString()}
                      </p>
                      <p className="text-xs md:text-sm text-muted-foreground">
                        m² terreno
                      </p>
                    </div>
                  )}
                  {property.construction_size_m2 && (
                    <div className="bg-muted rounded-lg p-3 md:p-4">
                      <Square className="w-5 h-5 md:w-6 md:h-6 text-muted-foreground mb-2" />
                      <p className="text-lg md:text-2xl font-bold">
                        {property.construction_size_m2.toLocaleString()}
                      </p>
                      <p className="text-xs md:text-sm text-muted-foreground">
                        m² construcción
                      </p>
                    </div>
                  )}
                  {property.parking && (
                    <div className="bg-muted rounded-lg p-3 md:p-4">
                      <svg
                        className="w-5 h-5 md:w-6 md:h-6 text-muted-foreground mb-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"
                        />
                      </svg>
                      <p className="text-lg md:text-2xl font-bold">
                        {property.parking}
                      </p>
                      <p className="text-xs md:text-sm text-muted-foreground">
                        Estacionamientos
                      </p>
                    </div>
                  )}
                </div>

                {/* Fila 2: Recámaras, Baños, Medios Baños */}
                <div className="grid grid-cols-3 gap-3 md:gap-4">
                  {property.bedrooms && (
                    <div className="bg-muted rounded-lg p-3 md:p-4">
                      <Bed className="w-5 h-5 md:w-6 md:h-6 text-muted-foreground mb-2" />
                      <p className="text-lg md:text-2xl font-bold">
                        {property.bedrooms}
                      </p>
                      <p className="text-xs md:text-sm text-muted-foreground">
                        Recámaras
                      </p>
                    </div>
                  )}
                  {property.bathrooms && (
                    <div className="bg-muted rounded-lg p-3 md:p-4">
                      <Bath className="w-5 h-5 md:w-6 md:h-6 text-muted-foreground mb-2" />
                      <p className="text-lg md:text-2xl font-bold">
                        {property.bathrooms}
                      </p>
                      <p className="text-xs md:text-sm text-muted-foreground">
                        Baños
                      </p>
                    </div>
                  )}
                  {property.half_bathrooms > 0 && (
                    <div className="bg-muted rounded-lg p-3 md:p-4">
                      <Bath className="w-5 h-5 md:w-6 md:h-6 text-muted-foreground mb-2" />
                      <p className="text-lg md:text-2xl font-bold">
                        {property.half_bathrooms}
                      </p>
                      <p className="text-xs md:text-sm text-muted-foreground">
                        Medios Baños
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div className="mb-6 md:mb-8">
                <h2
                  className="text-xl md:text-2xl font-bold mb-3 md:mb-4"
                  style={{ fontFamily: "Manrope, sans-serif" }}
                >
                  Descripción
                </h2>
                <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                  {property.description}
                </p>
              </div>

              {property.features && property.features.length > 0 && (
                <div className="mb-6 md:mb-8">
                  <h2
                    className="text-xl md:text-2xl font-bold mb-3 md:mb-4"
                    style={{ fontFamily: "Manrope, sans-serif" }}
                  >
                    Características
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 md:gap-3">
                    {property.features.map((feature, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <div className="w-2 h-2 rounded-full bg-accent flex-shrink-0"></div>
                        <span className="text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {property.amenities && property.amenities.length > 0 && (
                <div className="mb-6 md:mb-8">
                  <h2
                    className="text-xl md:text-2xl font-bold mb-3 md:mb-4"
                    style={{ fontFamily: "Manrope, sans-serif" }}
                  >
                    Amenidades
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 md:gap-3">
                    {property.amenities.map((amenity, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0"></div>
                        <span className="text-sm">{amenity}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {developer && (
                <div className="mb-6 md:mb-8">
                  <h2
                    className="text-xl md:text-2xl font-bold mb-3 md:mb-4"
                    style={{ fontFamily: "Manrope, sans-serif" }}
                  >
                    Desarrollador
                  </h2>
                  <div className="bg-gradient-to-br from-primary/5 to-accent/5 border border-primary/20 rounded-lg p-4 md:p-6">
                    <div className="flex items-start gap-4">
                      {developer.image_url && (
                        <img
                          src={developer.image_url}
                          alt={developer.name}
                          className="w-16 h-16 rounded-full object-cover flex-shrink-0"
                        />
                      )}
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3
                            className="text-lg font-bold"
                            style={{ fontFamily: "Manrope, sans-serif" }}
                          >
                            {developer.name}
                          </h3>
                          {developer.is_verified && (
                            <span className="px-3 py-1 bg-accent/20 text-accent text-xs font-semibold rounded-full flex items-center gap-1">
                              ✓ Verificado
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                          {developer.description}
                        </p>
                        {developer.specialties &&
                          developer.specialties.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-3">
                              {developer.specialties
                                .slice(0, 3)
                                .map((specialty, idx) => (
                                  <span
                                    key={idx}
                                    className="px-2 py-1 bg-background text-xs rounded-full border border-border"
                                  >
                                    {specialty}
                                  </span>
                                ))}
                            </div>
                          )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(`/developer/${developer.id}`)}
                          data-testid="view-developer-profile"
                        >
                          Ver Perfil Completo
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {property.geometry && (
                <div className="mb-6 md:mb-8">
                  <h2
                    className="text-xl md:text-2xl font-bold mb-3 md:mb-4"
                    style={{ fontFamily: "Manrope, sans-serif" }}
                  >
                    Ubicación
                  </h2>
                  {MAPBOX_TOKEN ? (
                    <div className="h-[300px] md:h-[400px] rounded-lg overflow-hidden">
                      <Map
                        longitude={property.geometry.coordinates[0]}
                        latitude={property.geometry.coordinates[1]}
                        zoom={14}
                        style={{ width: "100%", height: "100%" }}
                        mapStyle="mapbox://styles/mapbox/light-v11"
                        mapboxAccessToken={MAPBOX_TOKEN}
                      >
                        <NavigationControl position="top-right" />
                        <Marker
                          longitude={property.geometry.coordinates[0]}
                          latitude={property.geometry.coordinates[1]}
                        >
                          <div
                            style={{
                              width: "40px",
                              height: "40px",
                              borderRadius: "50%",
                              backgroundColor: "hsl(25, 45%, 25%)",
                              border: "3px solid white",
                              boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
                            }}
                          ></div>
                        </Marker>
                      </Map>
                    </div>
                  ) : (
                    <div className="h-[300px] md:h-[400px] rounded-lg border border-border bg-muted/30 flex items-center justify-center text-sm text-muted-foreground px-4 text-center">
                      El mapa no está disponible temporalmente. Configura
                      REACT_APP_MAPBOX_TOKEN en el entorno de despliegue.
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          </div>

          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-muted rounded-lg p-4 md:p-6 lg:sticky lg:top-24"
              id="property-contact-card"
            >
              <h3
                className="text-lg md:text-xl font-bold mb-4 md:mb-6"
                style={{ fontFamily: "Manrope, sans-serif" }}
              >
                Contactar
              </h3>
              <form
                onSubmit={handleContactSubmit}
                className="space-y-3 md:space-y-4"
                data-testid="contact-form"
              >
                <div>
                  <label className="text-xs md:text-sm font-medium mb-2 block">
                    Nombre *
                  </label>
                  <Input
                    type="text"
                    value={contactForm.name}
                    onChange={(e) =>
                      setContactForm({ ...contactForm, name: e.target.value })
                    }
                    required
                    className="h-10"
                    data-testid="contact-name"
                  />
                </div>
                <div>
                  <label className="text-xs md:text-sm font-medium mb-2 block">
                    Email *
                  </label>
                  <Input
                    type="email"
                    value={contactForm.email}
                    onChange={(e) =>
                      setContactForm({ ...contactForm, email: e.target.value })
                    }
                    required
                    className="h-10"
                    data-testid="contact-email"
                  />
                </div>
                <div>
                  <label className="text-xs md:text-sm font-medium mb-2 block">
                    Teléfono
                  </label>
                  <Input
                    type="tel"
                    value={contactForm.phone}
                    onChange={(e) =>
                      setContactForm({ ...contactForm, phone: e.target.value })
                    }
                    className="h-10"
                    data-testid="contact-phone"
                  />
                </div>
                <div>
                  <label className="text-xs md:text-sm font-medium mb-2 block">
                    Presupuesto Estimado
                  </label>
                  <select
                    value={contactForm.budget_range}
                    onChange={(e) =>
                      setContactForm({
                        ...contactForm,
                        budget_range: e.target.value,
                      })
                    }
                    className="w-full h-10 px-3 border border-input bg-background rounded-md text-sm"
                  >
                    <option value="">Selecciona un rango</option>
                    <option value="<1M">Menos de $1M MXN</option>
                    <option value="1M-3M">$1M - $3M MXN</option>
                    <option value="3M-5M">$3M - $5M MXN</option>
                    <option value="5M-10M">$5M - $10M MXN</option>
                    <option value="10M-25M">$10M - $25M MXN</option>
                    <option value="25M-50M">$25M - $50M MXN</option>
                    <option value=">50M">Más de $50M MXN</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs md:text-sm font-medium mb-2 block">
                    Horizonte de Compra
                  </label>
                  <select
                    value={contactForm.purchase_timeline}
                    onChange={(e) =>
                      setContactForm({
                        ...contactForm,
                        purchase_timeline: e.target.value,
                      })
                    }
                    className="w-full h-10 px-3 border border-input bg-background rounded-md text-sm"
                  >
                    <option value="">Selecciona tu horizonte</option>
                    <option value="inmediato">Inmediato (este mes)</option>
                    <option value="1-3-meses">1-3 meses</option>
                    <option value="3-6-meses">3-6 meses</option>
                    <option value="6+-meses">Más de 6 meses</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs md:text-sm font-medium mb-2 block">
                    Uso Previsto
                  </label>
                  <select
                    value={contactForm.intended_use}
                    onChange={(e) =>
                      setContactForm({
                        ...contactForm,
                        intended_use: e.target.value,
                      })
                    }
                    className="w-full h-10 px-3 border border-input bg-background rounded-md text-sm"
                  >
                    <option value="">Selecciona el uso</option>
                    <option value="vivir">Para vivir</option>
                    <option value="inversion">Inversión</option>
                    <option value="negocio">Negocio/Comercial</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs md:text-sm font-medium mb-2 block">
                    Mensaje *
                  </label>
                  <Textarea
                    value={contactForm.message}
                    onChange={(e) =>
                      setContactForm({
                        ...contactForm,
                        message: e.target.value,
                      })
                    }
                    rows={4}
                    required
                    data-testid="contact-message"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full h-11"
                  data-testid="contact-submit"
                >
                  Enviar Mensaje
                </Button>
              </form>
            </motion.div>
          </div>
        </div>
      </div>

      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 border-t border-border bg-background/95 backdrop-blur px-4 py-3">
        <div className="max-w-7xl mx-auto grid grid-cols-2 gap-2">
          <Button variant="outline" onClick={toggleFavorite}>
            <Heart
              className={`w-4 h-4 mr-2 ${isFavorite ? "fill-red-500 text-red-500" : ""}`}
            />
            {isFavorite ? "Guardada" : "Guardar"}
          </Button>
          <Button onClick={scrollToContact}>Contactar ahora</Button>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PropertyDetail;
